import React, { useState } from "react";
import {
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Paper,
  makeStyles,
} from "@material-ui/core";
import t from "../common/localization";
import {
  formatPosition,
  formatDistance,
  formatHours,
  formatDate,
  formatSpeed,
  formatVolume,
  formatDurationTime,
} from "../common/formatter";
import ReportFilter from "./ReportFilter";
import ReportLayoutPage from "./ReportLayoutPage";
import { useAttributePreference } from "../common/preferences";
import { showLoaderStateActions } from "./../store";
import { useDispatch, useSelector } from "react-redux";
import NavigationIcon from "@material-ui/icons/Navigation";
import Chip from "@material-ui/core/Chip";
import SpeedIcon from "@material-ui/icons/Speed";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import ContactMailIcon from "@material-ui/icons/ContactMail";
import AcUnitIcon from "@material-ui/icons/AcUnit";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import { filterErrorMessage } from "../common/stringUtils";
import { DataGrid } from "@material-ui/data-grid";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },

  content: {
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(2),
    height: "100%",
    width: "100%",
    backgroundColor: "#EFF2F8",
    borderRadius: "5px",
  },
  paper: {
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
    width: "100%",
  },
  mapContainer: {
    flexGrow: 5,
    boxShadow: "-5px -5px 5px 5px #b9bcdf",
    borderRadius: "10px",
  },
  form: {
    padding: theme.spacing(1, 2, 2),
  },
  tableRow: {
    "&$hover:hover": {
      backgroundColor: "#FFFFFF",
    },
  },
  tContainer: {
    maxHeight: 690,
  },
  tCell: {
    color: "white",
    backgroundColor: "#361DDB",
  },
  statusGridOn: {
    backgroundColor: "#C5E2C9",
    color: "green",
    borderRadius: "25px",
    paddingLeft: "7px",
    margin: theme.spacing(0.5),
  },
  statusGridOff: {
    backgroundColor: "#FFE9D1",
    color: "#ce5934",
    borderRadius: "25px",
    paddingLeft: "7px",
    margin: theme.spacing(0.5),
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const Filter = ({ setItems }) => {
  const dispatch = useDispatch();
  const handleSubmit = async (
    deviceId,
    groupId,
    from,
    to,
    mail,
    headers,
    onResponseGet
  ) => {
    const query = new URLSearchParams({ from, to, mail });
    let qString = query.toString();
    let deviceIdQuery = "";
    let groupIdQuery = "";

    deviceId.forEach((element) => {
      deviceIdQuery += `&deviceId=${element}`;
    });
    groupId.forEach((element) => {
      groupIdQuery += `&groupId=${element}`;
    });

    if (deviceIdQuery) {
      qString += deviceIdQuery;
    }
    if (groupIdQuery) {
      qString += groupIdQuery;
    }
    dispatch(showLoaderStateActions.addState(true));
    const response = await fetch(`/api/reports/route?${qString}`, { headers });
    onResponseGet(response);
    if (response.ok && response.headers.get("content-type")) {
      const contentType = response.headers.get("content-type");
      if (contentType === "application/pdf") {
        const name = "report.pdf";
        const blobUrl = URL.createObjectURL(await response.blob());
        // Create a link element
        const link = document.createElement("a");
        // Set link's href to point to the Blob URL
        link.href = blobUrl;
        link.download = name;
        // Append link to the body
        document.body.appendChild(link);
        // Dispatch click event on the link
        // This is necessary as link.click() does not work on the latest firefox
        link.dispatchEvent(
          new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window,
          })
        );
        // Remove link from body
        document.body.removeChild(link);
        dispatch(showLoaderStateActions.addState(false));
      } else if (contentType === "application/json") {
        const resItem = await response.json();
        dispatch(showLoaderStateActions.addState(false));
        setItems(resItem);
      } else {
        dispatch(showLoaderStateActions.addState(false));
        window.location.assign(
          window.URL.createObjectURL(await response.blob())
        );
      }
    } else {
      dispatch(showLoaderStateActions.addState(false));
    }
  };

  return (
    <>
      <ReportFilter handleSubmit={handleSubmit} reportHeader="Route Report" />
    </>
  );
};

const RouteReportPage = () => {
  const distanceUnit = useAttributePreference("distanceUnit");
  const speedUnit = useAttributePreference("speedUnit");
  const [items, setItems] = useState([]);
  const deviceData = useSelector((state) => state.devices.items);
  const classes = useStyles();

  let mainData = [];
  const setRecursiveData = (newItems) => {
    setTimeout(() => {
      let hasMore = mainData.length + 1 < newItems.length;
      const list = newItems.slice(0, mainData.length + 1);
      mainData = list;
      setItems(list);
      if (hasMore) setRecursiveData(newItems);
    }, 0);
  };

  const setData = (newItems) => {
    mainData = [];
    setRecursiveData(newItems);
  };

  const columns = [
    { field: "deviceName", headerName: t("settingsVehicleNumber"), flex: 1 },
    { field: "fixTime", headerName: t("positionFixTime"), flex: 1,
      renderCell: (params) => <Chip
        label={params.value}
        icon={<AccessTimeIcon />}
        size="small"
        color="secondary"
        style={{
          backgroundColor: "#EFEBFC",
          color: "#623DE6",
          borderRadius: 3,
          margin: "5px",
        }}
      />
    },
    { field: "totalDistance", headerName: t("sharedDistance"), flex: 1,
      renderCell: (params) => <Chip
        label={params.value}
        icon={<NavigationIcon />}
        size="small"
        color="secondary"
        style={{
          backgroundColor: "#D8ECDB",
          color: "#29A639",
          borderRadius: 3,
          margin: "5px",
        }}
      />
     },
    { 
      field: "speed", 
      headerName: t("positionSpeed"),
      flex: 1,
      renderCell: (params) => <Chip
        label={params.value}
        icon={<SpeedIcon />}
        size="small"
        color="secondary"
        style={{
          backgroundColor: "#D8ECDB",
          color: "#29A639",
          borderRadius: 3,
          margin: "5px",
        }}
      />,
    },
    { field: "ac", headerName: "AC", flex: 1, 
      renderCell: (params) => <Chip
        label={params.value}
        icon={<AcUnitIcon />}
        size="small"
        color="secondary"
        className={
          params.value
            ? classes.statusGridOn
            : classes.statusGridOff
        }
      />
    },
    { field: "totalDistance", headerName: "Odometer", flex: 1 },
    { field: "latitude", headerName: t("positionOdometer"), flex: 1 },
    { field: "longitude", headerName: t("positionLatitude"), flex: 1 },
    { field: "address", headerName: t("positionLongitude"), flex: 1 },
  ];  

  // Build DataGrid rows
  const gridRows = items.map((item, index) => ({
    id: index,
    deviceName: deviceData[item.deviceId]?.name || "N/A",
    fixTime: formatDate(item.fixTime, "MMM D, hh:mm A"),
    totalDistance: item.attributes ? formatDistance(item.attributes.distance, distanceUnit) : "0",
    speed: formatPosition(item, "speed"),
    ac: item.attributes?.ac ?? "Off",
    totalOdometer: item.attributes ? formatDistance(item.attributes.totalDistance, distanceUnit) : "0",
    latitude: formatPosition(item, "latitude"),
    longitude: formatPosition(item, "longitude"),
    address: formatPosition(item, "address"),
  }));

  return (
    <ReportLayoutPage filter={<Filter setItems={setData} />}>
      {/* <TableContainer className={classes.tContainer} component={Paper}>
        <Table
          stickyHeader
          aria-label="sticky table"
          style={{ whiteSpace: "nowrap" }}
        >
          <TableHead style={{ backgroundColor: "#361DDB" }}>
            <TableRow>
              <TableCell className={classes.tCell}>
                {t("settingsVehicleNumber")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("positionFixTime")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("sharedDistance")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("positionSpeed")}
              </TableCell>
              <TableCell className={classes.tCell}>{"AC"}</TableCell>
              <TableCell className={classes.tCell}>
                {t("positionOdometer")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("positionLatitude")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("positionLongitude")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("positionAddress")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow hover key={item.id} className={classes.tableRow}>
                <TableCell>
                  <b>{deviceData[item.deviceId]?.name}</b>
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatDate(item.fixTime, "MMM D, hh:mm A")}
                    icon={<AccessTimeIcon />}
                    size="small"
                    color="secondary"
                    style={{
                      backgroundColor: "#EFEBFC",
                      color: "#623DE6",
                      borderRadius: 3,
                      margin: "5px",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatDistance(
                      item.attributes.distance,
                      distanceUnit
                    )}
                    icon={<NavigationIcon />}
                    size="small"
                    color="secondary"
                    style={{
                      backgroundColor: "#D8ECDB",
                      color: "#29A639",
                      borderRadius: 3,
                      margin: "5px",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatPosition(item, "speed")}
                    icon={<SpeedIcon />}
                    size="small"
                    color="secondary"
                    style={{
                      backgroundColor: "#D8ECDB",
                      color: "#29A639",
                      borderRadius: 3,
                      margin: "5px",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={item.attributes.ac}
                    icon={<AcUnitIcon />}
                    size="small"
                    color="secondary"
                    className={
                      item.attributes.ac
                        ? classes.statusGridOn
                        : classes.statusGridOff
                    }
                  />
                </TableCell>
                <TableCell>
                  {formatDistance(item.attributes.totalDistance, distanceUnit)}
                </TableCell>
                <TableCell>{formatPosition(item, "latitude")}</TableCell>
                <TableCell>{formatPosition(item, "longitude")}</TableCell>
                <TableCell>{formatPosition(item, "address")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer> */}
      <DataGrid
        rows={gridRows}
        columns={columns}
        disableSelectionOnClick
        autoHeight={false}
        pageSize={50}
        rowsPerPageOptions={[50, 100, 500]}
        style={{ height: "85vh", width: "100%", backgroundColor: "white" }}
        pagination
      />
    </ReportLayoutPage>
  );
};

export default RouteReportPage;
